import { useEffect, useMemo, useState } from 'react'
import { AlertTriangle, ArrowLeft, CheckCircle2, Loader2, MapPin, Plus, Search, Trash2, UserPlus, X } from 'lucide-react'
import { Button } from '@/components/ui'
import {
  fetchOnboardingCities,
  fetchLandGroups,
  fetchPopulationGroups,
  fetchCityTimezones,
  fetchPreopenStudios,
  fetchRegisteredPreopenStudios,
  onboardPartnerNewStudio,
  onboardPartnerStudioPurchase,
  openStudioFromOriginalCase,
  planNewStudioForPartner,
  registerPreopenStudio,
  type OnboardingCity,
  type OnboardingPersonInput,
  type LandGroup,
  type PopulationGroup,
  type PreopenStudio,
} from '@/services/studioOnboardingService'
import {
  fetchStudioPurchaseCandidates,
  fetchStudioPurchasePartners,
  type StudioPurchaseCandidate,
  type StudioPurchasePartner,
} from '@/services/studioPurchaseService'

/**
 * Онбординг на экране «Исходные данные»: франчайзи и студии заводят здесь,
 * а договор — кнопкой «Новый ДКК» в реестре документов (решение владельца 03.08.2026).
 */
export type StudioOnboardingMode = 'partner' | 'studio' | 'register' | 'open'

export type StudioOnboardingInitialData = {
  payload?: Record<string, unknown>
  studioInternalId?: number | null
  changedAt?: string
}

const MODE_LABELS: Record<StudioOnboardingMode, string> = {
  partner: 'Новый франчайзи',
  studio: 'Новая студия франчайзи',
  register: 'Номер и адрес',
  open: 'Фактическое открытие',
}

const INPUT_CLASS =
  'mt-1 w-full rounded border border-border bg-background px-2 py-2 text-sm text-foreground outline-none focus:border-primary'

type PersonDraft = { firstName: string; lastName: string; gender: '' | 'м' | 'ж' }

function emptyPerson(): PersonDraft {
  return { firstName: '', lastName: '', gender: '' }
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const message = String((error as { message?: unknown }).message ?? '').trim()
    if (message) return message
  }
  return fallback
}

function matchesQuery(haystack: Array<string | number | null | undefined>, query: string): boolean {
  const trimmed = query.trim().toLowerCase()
  if (!trimmed) return true
  return haystack
    .filter(value => value !== null && value !== undefined && value !== '')
    .join(' ')
    .toLowerCase()
    .includes(trimmed)
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function dateAtNoon(value: string): string | null {
  return value ? `${value} 12:00:00` : null
}

export function StudioOnboardingModal({
  initialMode = 'partner',
  initialPartnerTarget = 'new_studio',
  initialData,
  onBack,
  onClose,
  onDone,
}: {
  initialMode?: StudioOnboardingMode
  initialPartnerTarget?: 'new_studio' | 'buy_studio'
  initialData?: StudioOnboardingInitialData
  onBack?: () => void
  onClose: () => void
  onDone?: () => void
}) {
  const [mode, setMode] = useState<StudioOnboardingMode>(initialMode)

  const [cities, setCities] = useState<OnboardingCity[]>([])
  const [populationGroups, setPopulationGroups] = useState<PopulationGroup[]>([])
  const [landGroups, setLandGroups] = useState<LandGroup[]>([])
  const [cityTimezones, setCityTimezones] = useState<string[]>(['Europe/Moscow'])
  const [partners, setPartners] = useState<StudioPurchasePartner[]>([])
  const [studios, setStudios] = useState<StudioPurchaseCandidate[]>([])
  const [preopenStudios, setPreopenStudios] = useState<PreopenStudio[]>([])
  const [registeredPreopenStudios, setRegisteredPreopenStudios] = useState<PreopenStudio[]>([])
  const [loading, setLoading] = useState(true)

  // Общее: персоны и партнёр
  const initialPayload = initialData?.payload ?? {}
  const payloadText = (key: string) => typeof initialPayload[key] === 'string' ? initialPayload[key] as string : ''
  const payloadNumber = (key: string) => {
    const value = initialPayload[key]
    const numberValue = typeof value === 'number' ? value : Number(value)
    return Number.isFinite(numberValue) ? numberValue : null
  }
  const payloadPersons: PersonDraft[] = Array.isArray(initialPayload.partner_group_persons)
    ? initialPayload.partner_group_persons
      .filter((person): person is Record<string, unknown> => person !== null && typeof person === 'object')
      .map(person => {
        const gender: PersonDraft['gender'] = person.gender === 'м' || person.gender === 'ж' ? person.gender : ''
        return {
          firstName: typeof person.first_name === 'string' ? person.first_name : '',
          lastName: typeof person.last_name === 'string' ? person.last_name : '',
          gender,
        }
      })
      .filter(person => person.firstName.trim() && person.lastName.trim())
    : []
  const [persons, setPersons] = useState<PersonDraft[]>(payloadPersons.length > 0 ? payloadPersons : [emptyPerson()])
  const [attachToExisting, setAttachToExisting] = useState(payloadNumber('partner_number') !== null)
  const [partnerQuery, setPartnerQuery] = useState('')
  const [partnerNumber, setPartnerNumber] = useState(() => payloadNumber('partner_number')?.toString() ?? '')

  // Город
  const [cityMode, setCityMode] = useState<'existing' | 'new'>(() => payloadText('city_name') ? 'new' : 'existing')
  const [cityQuery, setCityQuery] = useState('')
  const [cityId, setCityId] = useState('')
  const [newCityName, setNewCityName] = useState(() => payloadText('city_name'))
  const [newCountry, setNewCountry] = useState(() => payloadText('country') || 'РФ')
  const [populationThousands, setPopulationThousands] = useState(() => payloadNumber('population_thousands')?.toString() ?? '')
  const [newTimezone, setNewTimezone] = useState(() => payloadText('iana_timezone') || 'Europe/Moscow')
  const [cityPopulationGroup, setCityPopulationGroup] = useState(() => payloadNumber('city_population_group')?.toString() ?? '')
  const [cityLandGroup, setCityLandGroup] = useState(() => payloadNumber('city_land_group')?.toString() ?? '')

  // Новый франчайзи: цель — новая студия или покупка существующей
  const [partnerTarget, setPartnerTarget] = useState<'new_studio' | 'buy_studio'>(initialPartnerTarget)
  const [studioQuery, setStudioQuery] = useState('')
  const [studioInternalId, setStudioInternalId] = useState(() => initialData?.studioInternalId?.toString() ?? '')

  // Новая студия партнёру
  const [newSeries, setNewSeries] = useState(false)

  // Номер и адрес
  const [registerStudioId, setRegisterStudioId] = useState(() => initialData?.studioInternalId?.toString() ?? '')
  const [address, setAddress] = useState('')
  const [manualStudioNumber, setManualStudioNumber] = useState('')

  // Фактическое открытие
  const [openStudioId, setOpenStudioId] = useState(() => initialData?.studioInternalId?.toString() ?? '')
  const [openStudioQuery, setOpenStudioQuery] = useState('')
  const [openDate, setOpenDate] = useState(() => initialData?.changedAt ?? todayIso())

  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  async function loadReferences() {
    setLoading(true)
    try {
      const [cityRows, populationGroupRows, landGroupRows, timezoneRows, partnerRows, studioRows, preopenRows, registeredPreopenRows] = await Promise.all([
        fetchOnboardingCities(),
        fetchPopulationGroups(),
        fetchLandGroups(),
        fetchCityTimezones(),
        fetchStudioPurchasePartners(),
        fetchStudioPurchaseCandidates(),
        fetchPreopenStudios(),
        fetchRegisteredPreopenStudios(),
      ])
      setCities(cityRows)
      setPopulationGroups(populationGroupRows)
      setLandGroups(landGroupRows)
      setCityTimezones(Array.from(new Set(['Europe/Moscow', ...timezoneRows])))
      setPartners(partnerRows)
      setStudios(studioRows)
      setPreopenStudios(preopenRows)
      setRegisteredPreopenStudios(registeredPreopenRows)
      setError(null)
    } catch (loadError: unknown) {
      setError(errorMessage(loadError, 'Не удалось загрузить справочники'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadReferences()
  }, [])

  useEffect(() => {
    if (populationThousands && !cityPopulationGroup) {
      const population = Number(populationThousands)
      const matchingGroup = populationGroups
        .filter(group => group.population_threshold <= population)
        .slice(-1)[0]
      if (matchingGroup) setCityPopulationGroup(String(matchingGroup.population_threshold))
    }
  }, [populationThousands, populationGroups, cityPopulationGroup])

  const filteredCities = useMemo(
    () => cities.filter(row => matchesQuery([row.city_name, row.country], cityQuery)).slice(0, 60),
    [cities, cityQuery]
  )

  const filteredPartners = useMemo(
    () => partners
      .filter(row => matchesQuery([row.partner_number, row.persons_fio, row.cities_text], partnerQuery))
      .slice(0, 60),
    [partners, partnerQuery]
  )

  const filteredStudios = useMemo(
    () => studios
      .filter(row => matchesQuery(
        [row.studio_number, row.city_name, row.address, row.owner_partner_number, row.owner_persons_fio],
        studioQuery
      ))
      .slice(0, 60),
    [studios, studioQuery]
  )

  const selectedRegisterStudio = useMemo(
    () => preopenStudios.find(row => String(row.studio_internal_id) === registerStudioId) ?? null,
    [preopenStudios, registerStudioId]
  )

  const filteredRegisteredPreopenStudios = useMemo(
    () => registeredPreopenStudios
      .filter(row => matchesQuery([
        row.studio_number,
        row.city_name,
        row.country,
        row.address,
        row.partner_number,
        row.persons_fio,
      ], openStudioQuery))
      .slice(0, 60),
    [openStudioQuery, registeredPreopenStudios]
  )

  const selectedOpenStudio = useMemo(
    () => registeredPreopenStudios.find(row => String(row.studio_internal_id) === openStudioId) ?? null,
    [openStudioId, registeredPreopenStudios]
  )

  const personsPayload: OnboardingPersonInput[] = useMemo(
    () => persons
      .filter(person => person.firstName.trim() && person.lastName.trim())
      .map(person => ({
        first_name: person.firstName.trim(),
        last_name: person.lastName.trim(),
        gender: person.gender || null,
      })),
    [persons]
  )

  const cityPayload = cityMode === 'existing'
    ? { cityId: cityId ? Number(cityId) : null, cityName: null, country: null, populationThousands: null, cityPopulationGroup: null, cityLandGroup: null }
    : {
        cityId: null,
        cityName: newCityName,
        country: newCountry,
        ianaTimezone: newTimezone,
        populationThousands: Number(populationThousands) || null,
        cityPopulationGroup: Number(cityPopulationGroup) || null,
        cityLandGroup: Number(cityLandGroup) || null,
      }

  const cityReady = cityMode === 'existing'
    ? Boolean(cityId)
    : Boolean(newCityName.trim() && newCountry.trim() && newTimezone.trim())

  const canSubmit = (() => {
    if (saving || loading) return false
    if (mode === 'partner') {
      const partnerReady = attachToExisting ? Boolean(partnerNumber) : personsPayload.length > 0
      if (!partnerReady) return false
      return partnerTarget === 'new_studio' ? cityReady : Boolean(studioInternalId)
    }
    if (mode === 'studio') return Boolean(partnerNumber) && cityReady
    if (mode === 'register') return Boolean(registerStudioId) && Boolean(address.trim())
    return Boolean(openStudioId && openDate && selectedOpenStudio?.studio_number)
  })()

  function resetAfterSuccess() {
    setPersons([emptyPerson()])
    setNote('')
    setAddress('')
    setManualStudioNumber('')
    setRegisterStudioId('')
    setStudioInternalId('')
    setOpenStudioId('')
  }

  async function handleSubmit() {
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      if (mode === 'partner' && partnerTarget === 'new_studio') {
        const result = await onboardPartnerNewStudio({
          persons: attachToExisting ? personsPayload : personsPayload,
          existingPartnerNumber: attachToExisting ? Number(partnerNumber) : null,
          ...cityPayload,
          note,
        })
        setSuccess(
          `${result.created_partner ? `Заведён франчайзи №${result.partner_number}` : `Франчайзи №${result.partner_number}`}, `
          + `серия ${result.series_id}, студия-заготовка ${result.studio_internal_id} в фазе PLANNED. `
          + 'Дальше: присвойте номер и адрес, затем заведите ДКК в реестре документов.'
        )
      } else if (mode === 'partner') {
        const result = await onboardPartnerStudioPurchase({
          persons: personsPayload,
          studioInternalId: Number(studioInternalId),
          existingPartnerNumber: attachToExisting ? Number(partnerNumber) : null,
          note,
        })
        setSuccess(
          `${result.created_partner ? `Заведён франчайзи №${result.partner_number}` : `Франчайзи №${result.partner_number}`}, `
          + `серия ${result.series_id}, владение ${result.ownership_id} в фазе BUYING на студию №${result.studio_number}. `
          + 'Дальше: ДКК в реестре документов, затем «Провести покупку».'
        )
      } else if (mode === 'studio') {
        const result = await planNewStudioForPartner({
          partnerNumber: Number(partnerNumber),
          ...cityPayload,
          newSeries,
          note,
        })
        setSuccess(
          `Студия-заготовка ${result.studio_internal_id} в фазе PLANNED, серия ${result.series_id}. `
          + 'Дальше: присвойте номер и адрес, затем заведите ДКК в реестре документов.'
        )
      } else if (mode === 'register') {
        const result = await registerPreopenStudio({
          studioInternalId: Number(registerStudioId),
          address,
          studioNumber: manualStudioNumber ? Number(manualStudioNumber) : null,
          note,
        })
        setSuccess(`Студии присвоен номер №${result.studio_number}, адрес «${result.address}».`)
      } else {
        if (!selectedOpenStudio?.studio_number) {
          throw new Error('Для открытия нужен номер студии')
        }

        const result = await openStudioFromOriginalCase({
          studioNumber: String(selectedOpenStudio.studio_number),
          changedAt: dateAtNoon(openDate) ?? '',
        })
        setSuccess(
          `Студия №${result.studio_number} открыта. Владение переведено в статус «владеет».`
          + (result.group_created ? ' Группа ШМП по городу добавлена.' : '')
        )
      }

      resetAfterSuccess()
      await loadReferences()
      onDone?.()
    } catch (submitError: unknown) {
      setError(errorMessage(submitError, 'Операция не выполнена'))
    } finally {
      setSaving(false)
    }
  }

  const showPersons = mode === 'partner'
  const showPartnerPicker = mode === 'studio' || (mode === 'partner' && attachToExisting)
  const showCity = mode === 'studio' || (mode === 'partner' && partnerTarget === 'new_studio')

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <div
        className="my-4 w-full max-w-3xl space-y-3 rounded-xl bg-background p-4 shadow-lg"
        onClick={event => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
              title="Вернуться к выбору кейса"
              aria-label="Вернуться к выбору кейса"
            >
              <ArrowLeft size={16} />
            </button>
          )}
          <h3 className="flex items-center gap-1.5 text-sm font-semibold">
            <UserPlus size={15} />
            Франчайзи и студии
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Закрыть"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex flex-wrap gap-1">
          {(Object.keys(MODE_LABELS) as StudioOnboardingMode[]).map(value => (
            <Button
              key={value}
              size="sm"
              variant={mode === value ? 'primary' : 'outline'}
              onClick={() => {
                setMode(value)
                setError(null)
                setSuccess(null)
              }}
            >
              {MODE_LABELS[value]}
              {value === 'register' && preopenStudios.length > 0 ? ` (${preopenStudios.length})` : ''}
              {value === 'open' && registeredPreopenStudios.length > 0 ? ` (${registeredPreopenStudios.length})` : ''}
            </Button>
          ))}
        </div>

        <p className="text-xs text-muted-foreground">
          Договор здесь не создаётся ни в одном из путей — иначе в серии появился бы пустой активный ДКК.
          Заведите его отдельно в реестре документов кнопкой «Новый ДКК» → «Договор в серию».
        </p>

        {loading ? (
          <div className="flex items-center gap-2 rounded border border-border bg-muted/30 px-3 py-4 text-sm text-muted-foreground">
            <Loader2 size={15} className="animate-spin" />
            Загружаем справочники…
          </div>
        ) : (
          <div className="space-y-3">
            {mode === 'partner' && (
              <div className="flex flex-wrap gap-1 rounded-md border border-border bg-muted/30 p-1 text-sm">
                {([
                  ['new_studio', 'Открывает новую студию'],
                  ['buy_studio', 'Покупает существующую'],
                ] as const).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setPartnerTarget(value)}
                    className={`rounded px-3 py-1.5 ${partnerTarget === value ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}

            {showPersons && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={attachToExisting}
                    onChange={event => setAttachToExisting(event.target.checked)}
                  />
                  Франчайзи уже заведён — добавить владение действующему
                </label>

                {!attachToExisting && (
                  <div className="space-y-2 rounded-md border border-border bg-muted/20 p-2">
                    <div className="text-xs font-semibold text-foreground">Персоны франчайзи</div>
                    {persons.map((person, index) => (
                      <div key={index} className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_90px_32px]">
                        <input
                          value={person.lastName}
                          onChange={event => setPersons(list => list.map((item, i) =>
                            i === index ? { ...item, lastName: event.target.value } : item
                          ))}
                          placeholder="Фамилия"
                          className="rounded border border-border bg-background px-2 py-2 text-sm outline-none focus:border-primary"
                        />
                        <input
                          value={person.firstName}
                          onChange={event => setPersons(list => list.map((item, i) =>
                            i === index ? { ...item, firstName: event.target.value } : item
                          ))}
                          placeholder="Имя"
                          className="rounded border border-border bg-background px-2 py-2 text-sm outline-none focus:border-primary"
                        />
                        <select
                          value={person.gender}
                          onChange={event => setPersons(list => list.map((item, i) =>
                            i === index ? { ...item, gender: event.target.value as PersonDraft['gender'] } : item
                          ))}
                          className="rounded border border-border bg-background px-2 py-2 text-sm outline-none focus:border-primary"
                        >
                          <option value="">пол</option>
                          <option value="м">м</option>
                          <option value="ж">ж</option>
                        </select>
                        <button
                          type="button"
                          onClick={() => setPersons(list => list.filter((_, i) => i !== index))}
                          disabled={persons.length === 1}
                          className="inline-flex h-9 w-8 items-center justify-center rounded border border-border text-muted-foreground hover:bg-red-50 hover:text-red-700 disabled:opacity-40"
                          aria-label="Убрать персону"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))}
                    <Button size="sm" variant="outline" onClick={() => setPersons(list => [...list, emptyPerson()])}>
                      <Plus size={13} className="mr-1" />
                      Ещё персона
                    </Button>
                  </div>
                )}
              </div>
            )}

            {showPartnerPicker && (
              <label className="block text-xs font-medium text-muted-foreground">
                Франчайзи
                <div className="mt-1 flex h-9 items-center gap-1 rounded border border-border bg-background px-2 focus-within:border-primary">
                  <Search size={14} className="shrink-0 text-muted-foreground" />
                  <input
                    value={partnerQuery}
                    onChange={event => setPartnerQuery(event.target.value)}
                    placeholder="номер, ФИО, город"
                    className="min-w-0 flex-1 bg-transparent text-sm font-normal text-foreground outline-none"
                  />
                </div>
                <select
                  value={partnerNumber}
                  onChange={event => setPartnerNumber(event.target.value)}
                  size={5}
                  className="mt-1 w-full rounded border border-border bg-background px-2 py-1 text-sm outline-none focus:border-primary"
                >
                  {filteredPartners.map(row => (
                    <option key={row.partner_number} value={String(row.partner_number)}>
                      №{row.partner_number} · {row.persons_fio || 'без персон'}
                      {row.current_status ? ` · ${row.current_status}` : ''}
                      {row.active_studio_count ? ` · студий: ${row.active_studio_count}` : ''}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {mode === 'open' && (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_180px]">
                <label className="block text-xs font-medium text-muted-foreground">
                  Студия
                  <div className="mt-1 flex h-9 items-center gap-1 rounded border border-border bg-background px-2 focus-within:border-primary">
                    <Search size={14} className="shrink-0 text-muted-foreground" />
                    <input
                      value={openStudioQuery}
                      onChange={event => setOpenStudioQuery(event.target.value)}
                      placeholder="номер, город, адрес, франчайзи"
                      className="min-w-0 flex-1 bg-transparent text-sm text-foreground outline-none"
                    />
                  </div>
                  <select
                    value={openStudioId}
                    onChange={event => setOpenStudioId(event.target.value)}
                    size={6}
                    className={INPUT_CLASS}
                  >
                    {filteredRegisteredPreopenStudios.map(studio => (
                      <option key={studio.studio_internal_id} value={studio.studio_internal_id}>
                        №{studio.studio_number} · {studio.city_name ?? 'город не указан'} · {studio.address ?? 'адрес не указан'}
                        {studio.partner_number ? ` · №${studio.partner_number} ${studio.persons_fio}` : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-xs font-medium text-muted-foreground">
                  Дата открытия
                  <input
                    type="date"
                    value={openDate}
                    onChange={event => setOpenDate(event.target.value)}
                    className={INPUT_CLASS}
                  />
                </label>
              </div>
            )}

            {mode === 'partner' && partnerTarget === 'buy_studio' && (
              <label className="block text-xs font-medium text-muted-foreground">
                Какую студию покупает
                <div className="mt-1 flex h-9 items-center gap-1 rounded border border-border bg-background px-2 focus-within:border-primary">
                  <Search size={14} className="shrink-0 text-muted-foreground" />
                  <input
                    value={studioQuery}
                    onChange={event => setStudioQuery(event.target.value)}
                    placeholder="номер, город, адрес, владелец"
                    className="min-w-0 flex-1 bg-transparent text-sm font-normal text-foreground outline-none"
                  />
                </div>
                <select
                  value={studioInternalId}
                  onChange={event => setStudioInternalId(event.target.value)}
                  size={5}
                  className="mt-1 w-full rounded border border-border bg-background px-2 py-1 text-sm outline-none focus:border-primary"
                >
                  {filteredStudios.map(row => (
                    <option key={row.studio_internal_id} value={String(row.studio_internal_id)}>
                      №{row.studio_number ?? row.studio_internal_id} · {row.city_name ?? 'город не указан'}
                      {row.owner_partner_number ? ` · владеет №${row.owner_partner_number} ${row.owner_persons_fio}` : ''}
                    </option>
                  ))}
                </select>
                <span className="mt-1 block text-[11px] font-normal text-muted-foreground">
                  Заведёт владение в фазе BUYING. Дата передачи ставится позже, кнопкой «Провести покупку».
                </span>
              </label>
            )}

            {showCity && (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-1 rounded-md border border-border bg-muted/30 p-1 text-sm">
                  {([
                    ['existing', 'Город из справочника'],
                    ['new', 'Новый город'],
                  ] as const).map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setCityMode(value)}
                      className={`rounded px-3 py-1.5 ${cityMode === value ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                {cityMode === 'existing' ? (
                  <label className="block text-xs font-medium text-muted-foreground">
                    Город
                    <div className="mt-1 flex h-9 items-center gap-1 rounded border border-border bg-background px-2 focus-within:border-primary">
                      <MapPin size={14} className="shrink-0 text-muted-foreground" />
                      <input
                        value={cityQuery}
                        onChange={event => setCityQuery(event.target.value)}
                        placeholder="город или страна"
                        className="min-w-0 flex-1 bg-transparent text-sm font-normal text-foreground outline-none"
                      />
                    </div>
                    <select
                      value={cityId}
                      onChange={event => setCityId(event.target.value)}
                      size={5}
                      className="mt-1 w-full rounded border border-border bg-background px-2 py-1 text-sm outline-none focus:border-primary"
                    >
                      {filteredCities.map(row => (
                        <option key={row.city_id} value={String(row.city_id)}>
                          {row.city_name} · {row.country}
                          {row.studio_count ? ` · студий: ${row.studio_count}` : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <label className="text-xs font-medium text-muted-foreground">
                      Название города
                      <input
                        value={newCityName}
                        onChange={event => setNewCityName(event.target.value)}
                        placeholder="Ярославль"
                        className={INPUT_CLASS}
                      />
                    </label>
                    <label className="text-xs font-medium text-muted-foreground">
                      Страна
                      <input
                        value={newCountry}
                        onChange={event => setNewCountry(event.target.value)}
                        placeholder="РФ"
                        className={INPUT_CLASS}
                      />
                    </label>
                    <label className="text-xs font-medium text-muted-foreground">
                      Население, тыс. чел.
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={populationThousands}
                        onChange={event => {
                          const value = event.target.value
                          setPopulationThousands(value)
                          const population = Number(value)
                          const matchingGroup = populationGroups
                            .filter(group => group.population_threshold <= population)
                            .slice(-1)[0]
                          setCityPopulationGroup(matchingGroup ? String(matchingGroup.population_threshold) : '')
                        }}
                        className={INPUT_CLASS}
                      />
                    </label>
                    <label className="text-xs font-medium text-muted-foreground">
                      Группа населения
                      <select value={cityPopulationGroup} onChange={event => setCityPopulationGroup(event.target.value)} className={INPUT_CLASS}>
                        <option value="">— выберите группу —</option>
                        {populationGroups.map(group => (
                          <option key={group.population_threshold} value={String(group.population_threshold)}>
                            {group.population_threshold} — {group.description}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-xs font-medium text-muted-foreground">
                      Земельная группа
                      <select value={cityLandGroup} onChange={event => setCityLandGroup(event.target.value)} className={INPUT_CLASS}>
                        <option value="">— выберите группу —</option>
                        {landGroups.map(group => (
                          <option key={group.land_group_code} value={String(group.land_group_code)}>
                            {group.land_group_code} — {group.land_group_name}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-xs font-medium text-muted-foreground">
                      Часовой пояс
                      <select value={newTimezone} onChange={event => setNewTimezone(event.target.value)} className={INPUT_CLASS}>
                        {cityTimezones.map(timezone => <option key={timezone} value={timezone}>{timezone}</option>)}
                      </select>
                    </label>
                    <div className="sm:col-span-2 flex items-start gap-1.5 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                      <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                      <span>
                        Если города с таким названием и страной ещё нет, он будет создан. Население и группы
                        заполняются отдельно — без них город попадёт в отчёты без разрезов.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {mode === 'studio' && (
              <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                <input
                  type="checkbox"
                  checked={newSeries}
                  onChange={event => setNewSeries(event.target.checked)}
                />
                Отдельная серия под эту студию (иначе ляжет в последнюю серию франчайзи)
              </label>
            )}

            {mode === 'register' && (
              <div className="space-y-3">
                <label className="block text-xs font-medium text-muted-foreground">
                  Студия-заготовка
                  <select
                    value={registerStudioId}
                    onChange={event => setRegisterStudioId(event.target.value)}
                    className={INPUT_CLASS}
                  >
                    <option value="">— выберите —</option>
                    {preopenStudios.map(row => (
                      <option key={row.studio_internal_id} value={String(row.studio_internal_id)}>
                        id {row.studio_internal_id} · {row.city_name ?? 'город не указан'}
                        {row.partner_number ? ` · №${row.partner_number} ${row.persons_fio}` : ' · без владельца'}
                        {row.ownership_phase ? ` · ${row.ownership_phase}` : ''}
                      </option>
                    ))}
                  </select>
                  {preopenStudios.length === 0 && (
                    <span className="mt-1 block text-[11px] font-normal text-muted-foreground">
                      Заготовок без номера нет.
                    </span>
                  )}
                </label>

                {selectedRegisterStudio && (
                  <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
                    Серия {selectedRegisterStudio.series_id ?? '—'}, статус {selectedRegisterStudio.status_code ?? '—'}.
                    Номер присваивается следующим по порядку; вручную — только если нужно занять конкретный.
                  </div>
                )}

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-[2fr_1fr]">
                  <label className="text-xs font-medium text-muted-foreground">
                    Адрес студии
                    <input
                      value={address}
                      onChange={event => setAddress(event.target.value)}
                      placeholder="ул. Строителей, д. 16"
                      className={INPUT_CLASS}
                    />
                  </label>
                  <label className="text-xs font-medium text-muted-foreground">
                    Номер вручную
                    <input
                      value={manualStudioNumber}
                      onChange={event => setManualStudioNumber(event.target.value)}
                      inputMode="numeric"
                      placeholder="авто"
                      className={INPUT_CLASS}
                    />
                  </label>
                </div>
              </div>
            )}

            <label className="block text-xs font-medium text-muted-foreground">
              Заметка
              <textarea
                value={note}
                onChange={event => setNote(event.target.value)}
                rows={2}
                placeholder="основание, номер сделки, договорённости"
                className="mt-1 w-full resize-none rounded border border-border bg-background px-2 py-2 text-sm text-foreground outline-none focus:border-primary"
              />
            </label>
          </div>
        )}

        {error && (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
        )}

        {success && (
          <div className="flex items-start gap-1.5 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
            <CheckCircle2 size={13} className="mt-0.5 shrink-0" />
            <span>{success}</span>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={onClose}>
            Закрыть
          </Button>
          <Button size="sm" disabled={!canSubmit} onClick={() => void handleSubmit()}>
            {saving
              ? <Loader2 size={14} className="mr-1 animate-spin" />
              : <UserPlus size={14} className="mr-1" />}
            {mode === 'register' ? 'Присвоить номер' : 'Завести'}
          </Button>
        </div>
      </div>
    </div>
  )
}
